import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Tags, Plus, Edit, Trash2, Loader2, Save, ChevronRight, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ExportListButtons } from './ExportListButtons';

export function AdminCategoriesPage() {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name: '', slug: '', type: 'service', parent_id: '', description: '',
    icon: '', order: 0, is_active: true
  });

  const fetchCategories = useCallback(async () => {
    try {
      const url = typeFilter ? `/api/admin/marketplace/categories?type=${typeFilter}` : '/api/admin/marketplace/categories';
      const { data } = await api.get(url);
      setCategories(data.categories || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, typeFilter]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = (parentId = '') => {
    const parent = parentId ? categories.find(c => c.id === parentId) : null;
    setEditing(null);
    setForm({
      name: '', slug: '',
      type: parent?.type === 'both' ? 'service' : (parent?.type || 'service'),
      parent_id: parentId,
      description: '', icon: '', order: 0, is_active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name, slug: cat.slug, type: cat.type, parent_id: cat.parent_id || '',
      description: cat.description || '', icon: cat.icon || '',
      order: cat.order || 0, is_active: cat.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) {
        await api.put(`/api/admin/marketplace/categories/${editing.id}`, payload);
        toast.success('Category updated');
      } else {
        await api.post('/api/admin/marketplace/categories', payload);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSubmitting(false);
  };

  const remove = async (cat) => {
    if (!window.confirm(`Deactivate category "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/admin/marketplace/categories/${cat.id}`);
      toast.success('Category deactivated');
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const [seeding, setSeeding] = useState(false);
  const seedDefaults = async () => {
    if (!window.confirm('Add all common default marketplace categories? This will only add categories that don\'t already exist (matched by slug). It will not modify or delete any of your existing categories.')) return;
    setSeeding(true);
    try {
      const { data } = await api.post('/api/admin/marketplace/categories/seed-defaults');
      toast.success(data.message || 'Defaults seeded');
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    }
    setSeeding(false);
  };

  // Group categories into tree for display
  const matchesSearch = (cat) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return cat.name.toLowerCase().includes(q) || (cat.slug || '').toLowerCase().includes(q);
  };
  // A root is visible if it matches OR any of its children match
  const visibleCategories = !search ? categories : categories.filter(c => {
    if (matchesSearch(c)) return true;
    // If this is a root, check if any child matches
    if (!c.parent_id) {
      return categories.some(child => child.parent_id === c.id && matchesSearch(child));
    }
    return false;
  });
  // Re-include root parents of matched sub-categories
  const finalCategories = !search ? categories : [...visibleCategories];
  if (search) {
    visibleCategories.filter(c => c.parent_id).forEach(child => {
      const parent = categories.find(c => c.id === child.parent_id);
      if (parent && !finalCategories.includes(parent)) finalCategories.push(parent);
    });
  }
  const roots = finalCategories.filter(c => !c.parent_id);
  const childrenOf = (parentId) => finalCategories.filter(c => c.parent_id === parentId);
  const rootOnly = finalCategories.filter(c => !c.parent_id);

  const typeBadge = (type) => {
    const map = {
      product: 'bg-[#FFEA00]/15 text-[#FFEA00]',
      service: 'bg-[#00E5FF]/15 text-[#00E5FF]',
      both: 'bg-[#7C4DFF]/15 text-[#7C4DFF]',
    };
    return <Badge className={`${map[type] || ''} text-[10px] uppercase`}>{type}</Badge>;
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-categories-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-semibold text-white flex items-center gap-3">
              <Tags className="w-8 h-8 text-[#FFEA00]" /> Marketplace Categories
            </h1>
            <p className="text-[#A0B4CC] mt-1">Manage product and service categories (max 2 levels)</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportListButtons api={api} resource="marketplace_categories" filename="marketplace_categories" />
            <Button onClick={() => openCreate()} className="btn-primary" data-testid="open-create-category-btn">
              <Plus className="w-4 h-4 mr-2" /> New Top-level Category
            </Button>
          </div>
        </div>

        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#7C4DFF]/10 to-[#00E5FF]/10 border border-[#7C4DFF]/20 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#FFEA00] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-white font-medium text-sm">Seed Common Marketplace Categories</p>
              <p className="text-[#A0B4CC] text-xs mt-0.5">Adds 20+ industries (Food, Health, Tech, etc.) with 200+ sub-categories. Safe — only inserts what&apos;s missing, never overwrites your existing data.</p>
            </div>
          </div>
          <Button
            onClick={seedDefaults}
            disabled={seeding}
            variant="outline"
            className="border-[#7C4DFF]/40 text-[#7C4DFF] hover:bg-[#7C4DFF]/10 hover:text-[#7C4DFF]"
            data-testid="seed-default-categories-btn"
          >
            {seeding ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Seed Defaults</>
            )}
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-[#041A3B] border-white/10 text-white h-10 text-sm"
              data-testid="categories-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{ v: '', l: 'All' }, { v: 'product', l: 'Products' }, { v: 'service', l: 'Services' }, { v: 'both', l: 'Both' }].map(t => (
              <button
                key={t.v} onClick={() => setTypeFilter(t.v)}
                data-testid={`cat-type-filter-${t.v || 'all'}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  typeFilter === t.v ? 'bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40' : 'bg-[#041A3B] text-[#A0B4CC] border border-white/10 hover:border-white/30'
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
        ) : (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-0">
              {rootOnly.length === 0 ? (
                <div className="py-16 text-center" data-testid="no-categories">
                  <Tags className="w-12 h-12 text-[#5C7599] mx-auto mb-3 opacity-60" />
                  <p className="text-[#A0B4CC]">No categories yet. Create your first top-level category.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {roots.map(root => (
                    <div key={root.id} data-testid={`category-row-${root.id}`}>
                      <div className="flex items-center justify-between p-4 hover:bg-white/5">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 rounded bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF] font-mono text-xs flex-shrink-0">
                            {root.order ?? '·'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-medium">{root.name}</p>
                              {typeBadge(root.type)}
                              {!root.is_active && <Badge className="bg-[#FF1744]/20 text-[#FF1744]">Inactive</Badge>}
                            </div>
                            <p className="text-[#5C7599] text-xs">{root.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openCreate(root.id)} className="text-[#00E676] text-xs" data-testid={`add-subcat-${root.id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Sub
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(root)} className="text-[#00E5FF]"><Edit className="w-4 h-4" /></Button>
                          {root.is_active && (
                            <Button size="sm" variant="ghost" onClick={() => remove(root)} className="text-[#FF1744]"><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </div>
                      {childrenOf(root.id).map(child => (
                        <div key={child.id} className="flex items-center justify-between pl-12 pr-4 py-3 bg-[#000B18]/30 hover:bg-white/5" data-testid={`subcategory-row-${child.id}`}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <ChevronRight className="w-4 h-4 text-[#5C7599] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-white text-sm">{child.name}</p>
                                {typeBadge(child.type)}
                                {!child.is_active && <Badge className="bg-[#FF1744]/20 text-[#FF1744]">Inactive</Badge>}
                              </div>
                              <p className="text-[#5C7599] text-xs">{child.slug}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(child)} className="text-[#00E5FF]"><Edit className="w-3.5 h-3.5" /></Button>
                            {child.is_active && (
                              <Button size="sm" variant="ghost" onClick={() => remove(child)} className="text-[#FF1744]"><Trash2 className="w-3.5 h-3.5" /></Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editing ? 'Edit Category' : (form.parent_id ? 'New Sub-category' : 'New Category')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {form.parent_id && !editing && (
                <div className="p-2 rounded bg-[#00E5FF]/5 border border-[#00E5FF]/20 text-xs text-[#00E5FF]">
                  Parent: {rootOnly.find(c => c.id === form.parent_id)?.name}
                </div>
              )}
              <div>
                <Label className="text-white">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Mobile Phones" className="bg-[#000B18] border-white/20 text-white" data-testid="category-name-input" />
              </div>
              <div>
                <Label className="text-white">Type *</Label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full bg-[#000B18] border border-white/20 rounded-md p-2 text-white text-sm" data-testid="category-type-select">
                  <option value="service">Service</option>
                  <option value="product">Product</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div>
                <Label className="text-white">Slug (auto-generated if blank)</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="bg-[#000B18] border-white/20 text-white font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Order</Label>
                  <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-[#000B18] border-white/20 text-white" />
                </div>
                {editing && (
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
                      <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#00E5FF]" />
                      Active
                    </label>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={save} disabled={submitting} className="btn-primary" data-testid="save-category-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {editing ? 'Save' : 'Create'}</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}
